import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// ===== CONSTANTS & HELPERS =====

export const STATUS_META = {
  no_watermark_ready: { label: 'Sans watermark pret', tone: 'info' },
  upscale_queued: { label: 'Upscale en file', tone: 'warning' },
  upscale_in_progress: { label: 'Upscale en cours', tone: 'info', spinner: true },
  upscale_completed: { label: 'Upscale termine', tone: 'success' },
  completed: { label: 'Termine', tone: 'success' },
  queued: { label: 'En file', tone: 'warning' },
  processing: { label: 'En cours', tone: 'info', spinner: true },
  failed: { label: 'Echec', tone: 'danger' },
};

export const TONE_CLASSES = {
  success: 'text-emerald-600 bg-emerald-50 dark:text-emerald-200 dark:bg-emerald-500/10',
  warning: 'text-amber-600 bg-amber-50 dark:text-amber-200 dark:bg-amber-500/10',
  info: 'text-blue-600 bg-blue-50 dark:text-blue-200 dark:bg-blue-500/10',
  danger: 'text-rose-600 bg-rose-50 dark:text-rose-200 dark:bg-rose-500/10',
  default: 'text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-white/10',
};

export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const resolveStatusKey = (job) => {
  const derivedType =
    job?.type || (job?.cost_credits > 0 || job?.credit_cost > 0 ? 'upscale' : 'no_watermark');
  const raw = job?.status;
  if (!raw) return 'queued';
  if (raw === 'completed') {
    if (derivedType === 'no_watermark') return 'no_watermark_ready';
    return 'upscale_completed';
  }
  if (raw === 'queued' && derivedType === 'upscale') return 'upscale_queued';
  if (raw === 'processing' && derivedType === 'upscale') return 'upscale_in_progress';
  return raw;
};

// ===== MAIN COMPONENT =====

/**
 * JobCard - Unified Job Card Component
 * 
 * @param {Object} props
 * @param {Object} props.group - Group containing jobs and sora_id
 * @param {Function} props.onDownload - Download handler
 * @param {Function} props.onCopy - Copy URL handler
 * @param {Function} [props.onDelete] - Delete handler (optional)
 * @param {Function} [props.onRetry] - Retry handler (optional)
 * @param {Function} [props.onUpscale] - Upscale handler (optional)
 * @param {Object} [props.previewData] - Preview data with title, thumbnailUrl, previewUrl
 * @param {Function} [props.onHoverPreview] - Hover preview handler
 * @param {Function} [props.onRefreshStatus] - Refresh status handler
 * @param {string} [props.refreshingId] - ID of currently refreshing job
 * @param {Function} [props.onExpand] - Expand preview handler
 * @param {string} [props.downloadingId] - ID of currently downloading job
 * @param {number} [props.upscaleCost] - Cost of upscale in credits
 * @param {boolean} [props.isHighlighted] - Whether card is highlighted
 * @param {boolean} [props.compact] - Dashboard compact mode (simpler UI)
 */
const JobCard = ({
  group,
  onDownload,
  onCopy,
  onDelete,
  onRetry,
  onUpscale,
  previewData,
  onHoverPreview,
  onRefreshStatus,
  refreshingId,
  onExpand,
  downloadingId,
  upscaleCost,
  isHighlighted,
  compact = false, // Dashboard uses compact mode
}) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const displayUrl = useMemo(() => {
    if (group.sora_id && group.sora_id.startsWith('s_')) {
      return `https://sora.chatgpt.com/p/${group.sora_id}`;
    }
    return group.source_url;
  }, [group]);

  // Identify latest jobs for each type
  const { nwJob, upJob, latestJob } = useMemo(() => {
    const sorted = [...group.jobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const nw = sorted.find((j) => {
      const type = j.type || (j.cost_credits === 0 ? 'no_watermark' : 'upscale');
      return type === 'no_watermark';
    });
    const up = sorted.find((j) => {
      const type = j.type || (j.cost_credits > 0 ? 'upscale' : 'no_watermark');
      return type === 'upscale';
    });
    return { nwJob: nw, upJob: up, latestJob: sorted[0] };
  }, [group]);

  const thumbnail =
    previewData?.thumbnailUrl ||
    latestJob?.thumbnail_url ||
    nwJob?.thumbnail_url ||
    upJob?.thumbnail_url;
  const previewUrl =
    previewData?.previewUrl || latestJob?.preview_url || nwJob?.preview_url || upJob?.preview_url;

  useEffect(() => {
    setIsVideoLoaded(false);
  }, [previewUrl]);

  // Desktop job row renderer
  const renderJobRow = (job, typeLabel, icon, defaultAction) => {
    if (!job) {
      return (
        <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-white/5 p-2">
          <div className="flex items-center gap-2 text-slate-400">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-slate-200 dark:bg-white/10">
              <FontAwesomeIcon icon={icon} className="text-xs" />
            </div>
            <span className="text-xs font-medium">{typeLabel}</span>
          </div>
          {defaultAction}
        </div>
      );
    }

    const statusKey = resolveStatusKey(job);
    const derivedType = job?.type || (job?.cost_credits > 0 || job?.credit_cost > 0 ? 'upscale' : 'no_watermark');
    const meta = STATUS_META[statusKey] || { label: statusKey, tone: 'default' };
    const badgeClass = TONE_CLASSES[meta.tone] || TONE_CLASSES.default;
    const isRefreshing = refreshingId === job.runpod_job_id;
    
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-slate-50 dark:bg-white/5 p-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`flex h-6 w-6 items-center justify-center rounded ${
              meta.tone === 'success'
                ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300'
                : 'bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-slate-400'
            }`}
          >
            <FontAwesomeIcon icon={icon} className="text-xs" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{typeLabel}</p>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${badgeClass} px-1.5 py-0.5 rounded`}>
                {meta.spinner && <FontAwesomeIcon icon="spinner" spin className="mr-1" />}
                {meta.label}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          {/* Refresh button for processing jobs only */}
          {onRefreshStatus && job.runpod_job_id && derivedType === 'upscale' && 
           !['completed', 'failed'].includes(job.status) && (
            <button
              onClick={() => onRefreshStatus(job)}
              disabled={isRefreshing}
              className="flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 dark:bg-white/5 dark:text-slate-400 dark:hover:bg-indigo-500/20 dark:hover:text-indigo-300 transition"
              title="Forcer la mise à jour du statut"
            >
              <FontAwesomeIcon icon="sync" spin={isRefreshing} />
            </button>
          )}
          
          {job.result_url ? (
            <button
              onClick={() => onDownload(job)}
              disabled={downloadingId === job.job_id}
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold shadow-sm transition-colors ${
                downloadingId === job.job_id
                  ? 'bg-indigo-300 text-white cursor-wait'
                  : 'bg-indigo-600 text-white hover:bg-indigo-500'
              }`}
            >
              <FontAwesomeIcon
                icon={downloadingId === job.job_id ? 'spinner' : 'download'}
                spin={downloadingId === job.job_id}
              />
              {downloadingId === job.job_id ? 'En cours...' : 'Télécharger'}
            </button>
          ) : job.status === 'failed' && onRetry ? (
             <button
              onClick={() => onRetry(job)}
              className="rounded border border-rose-200 text-rose-600 px-3 py-1.5 text-xs font-semibold hover:bg-rose-50 dark:border-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10"
            >
              Réessayer
            </button>
          ) : null}

          {onDelete && (
            <button
              onClick={() => onDelete(job)}
              className="ml-1 p-1.5 text-slate-300 hover:text-rose-500 transition"
              title="Supprimer cette version"
            >
              <FontAwesomeIcon icon="trash" className="text-xs" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Mobile compact job row
  const renderMobileJobRow = (job, typeLabel, icon) => {
    const hasJob = !!job;
    const isCompleted = job?.status === 'completed';
    const isFailed = job?.status === 'failed';
    const isProcessing = ['queued', 'processing'].includes(job?.status);
    
    let statusBadge;
    let statusClass;
    
    if (!hasJob) {
      statusBadge = 'Non généré';
      statusClass = 'text-slate-400 bg-slate-100 dark:bg-white/5';
    } else if (isCompleted) {
      statusBadge = 'Prêt';
      statusClass = 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-500/10';
    } else if (isFailed) {
      statusBadge = 'Échec';
      statusClass = 'text-rose-600 bg-rose-50 dark:text-rose-400 dark:bg-rose-500/10';
    } else if (isProcessing) {
      statusBadge = 'En cours';
      statusClass = 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-500/10';
    } else {
      statusBadge = job?.status || 'Inconnu';
      statusClass = 'text-slate-500 bg-slate-100 dark:bg-white/5';
    }
    
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 dark:bg-white/5 px-2 py-1.5">
        {/* Left: Icon + Label + Status */}
        <div className="flex items-center gap-2 min-w-0">
          <div className={`flex h-5 w-5 items-center justify-center rounded ${
            isCompleted 
              ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' 
              : 'bg-slate-200 text-slate-400 dark:bg-white/10'
          }`}>
            <FontAwesomeIcon icon={icon} className="text-[9px]" />
          </div>
          <span className="text-[10px] font-medium text-slate-700 dark:text-slate-300 truncate">{typeLabel}</span>
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${statusClass}`}>
            {isProcessing && <FontAwesomeIcon icon="spinner" spin className="mr-1 text-[8px]" />}
            {statusBadge}
          </span>
        </div>
        
        {/* Right: Action button */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {isCompleted && job?.result_url ? (
            <button
              onClick={() => onDownload(job)}
              disabled={downloadingId === job.job_id}
              className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] font-semibold transition-colors ${
                downloadingId === job.job_id
                  ? 'bg-indigo-300 text-white cursor-wait'
                  : 'bg-indigo-600 text-white hover:bg-indigo-500'
              }`}
            >
              <FontAwesomeIcon
                icon={downloadingId === job.job_id ? 'spinner' : 'download'}
                spin={downloadingId === job.job_id}
                className="text-[9px]"
              />
            </button>
          ) : isFailed && onRetry ? (
            <button
              onClick={() => onRetry(job)}
              className="flex items-center gap-1 rounded border border-rose-300 px-2 py-1 text-[10px] font-semibold text-rose-500 hover:bg-rose-50 dark:border-rose-500/30 dark:hover:bg-rose-500/10"
            >
              <FontAwesomeIcon icon="redo" className="text-[8px]" />
            </button>
          ) : null}
          
          {hasJob && onDelete && (
            <button
              onClick={() => onDelete(job)}
              className="p-1 text-slate-300 hover:text-rose-500 transition"
            >
              <FontAwesomeIcon icon="trash" className="text-[9px]" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Find best available job for single download (Dashboard compact mode)
  const bestAvailableJob = useMemo(() => {
    if (upJob?.status === 'completed' && upJob?.result_url) return upJob;
    if (nwJob?.status === 'completed' && nwJob?.result_url) return nwJob;
    return null;
  }, [upJob, nwJob]);

  const isNoWatermarkReady = nwJob?.status === 'completed';
  const isUpscaledReady = upJob?.status === 'completed';

  // ===== COMPACT MODE (Dashboard) =====
  if (compact) {
    return (
      <div className={`group relative overflow-hidden rounded-2xl border bg-white dark:bg-slate-900/60 shadow-sm transition-all hover:shadow-md ${
        isHighlighted 
          ? 'border-indigo-500 ring-2 ring-indigo-500/50 animate-pulse' 
          : 'border-slate-200 dark:border-white/10'
      }`}>
        
        {/* MOBILE COMPACT */}
        <div className="flex sm:hidden">
          {/* Thumbnail */}
          <div
            className="relative flex-shrink-0 cursor-pointer bg-slate-100 dark:bg-black/20 overflow-hidden w-20 h-auto aspect-square"
            onClick={() => previewUrl && onExpand?.(previewUrl)}
          >
            {previewUrl ? (
              <>
                {!isVideoLoaded && thumbnail && (
                  <img src={thumbnail} alt="Thumbnail" className="absolute inset-0 h-full w-full object-cover" />
                )}
                <video
                  src={previewUrl}
                  className={`h-full w-full object-cover transition-opacity duration-300 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                  muted
                  loop
                  playsInline
                  onCanPlay={() => setIsVideoLoaded(true)}
                />
              </>
            ) : thumbnail ? (
              <img src={thumbnail} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-400">
                <FontAwesomeIcon icon="video" className="text-lg" />
              </div>
            )}
            
            {/* Badges */}
            <div className="absolute top-1 left-1 flex flex-col gap-0.5">
              {isUpscaledReady && (
                <span className="px-1 py-0.5 rounded text-[7px] font-bold bg-indigo-500/90 text-white">×2</span>
              )}
              {isNoWatermarkReady && !isUpscaledReady && (
                <span className="px-1 py-0.5 rounded text-[7px] font-bold bg-emerald-500/90 text-white">NW</span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-2 flex flex-col justify-between min-w-0">
            <div>
              <p className="text-[10px] font-semibold text-slate-900 dark:text-white truncate">
                {formatDateTime(latestJob.created_at)}
              </p>
            </div>
            
            {bestAvailableJob ? (
              <button
                onClick={() => onDownload(bestAvailableJob)}
                disabled={downloadingId === bestAvailableJob.job_id}
                className={`mt-1 flex items-center justify-center gap-1 rounded px-2 py-1 text-[10px] font-semibold transition-colors ${
                  downloadingId === bestAvailableJob.job_id
                    ? 'bg-indigo-300 text-white cursor-wait'
                    : 'bg-indigo-600 text-white hover:bg-indigo-500'
                }`}
              >
                <FontAwesomeIcon
                  icon={downloadingId === bestAvailableJob.job_id ? 'spinner' : 'download'}
                  spin={downloadingId === bestAvailableJob.job_id}
                  className="text-[9px]"
                />
                {isUpscaledReady ? 'Upscale' : 'Sans WM'}
              </button>
            ) : (
              <span className="mt-1 text-[9px] text-amber-500 font-medium">
                <FontAwesomeIcon icon="spinner" spin className="mr-1" />
                En cours...
              </span>
            )}
          </div>
        </div>

        {/* DESKTOP COMPACT */}
        <div className="hidden sm:block">
          {/* Preview */}
          <div
            className="relative cursor-pointer bg-slate-100 dark:bg-black/20 overflow-hidden aspect-video"
            onMouseEnter={() => onHoverPreview?.(latestJob)}
            onClick={() => previewUrl && onExpand?.(previewUrl)}
          >
            {previewUrl ? (
              <>
                {!isVideoLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800 z-20">
                    {thumbnail && (
                      <img src={thumbnail} alt="Thumbnail" className="absolute inset-0 h-full w-full object-cover opacity-50 blur-sm" />
                    )}
                    <FontAwesomeIcon icon="spinner" spin className="relative z-30 text-indigo-500 text-xl" />
                  </div>
                )}
                <video
                  src={previewUrl}
                  className={`h-full w-full object-cover transition-opacity duration-500 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                  muted
                  loop
                  playsInline
                  onCanPlay={() => setIsVideoLoaded(true)}
                  onMouseOver={(e) => isVideoLoaded && e.target.play()}
                  onMouseOut={(e) => {
                    e.target.pause();
                    e.target.currentTime = 0;
                  }}
                />
              </>
            ) : thumbnail ? (
              <img src={thumbnail} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-400">
                <FontAwesomeIcon icon="video" className="text-xl" />
              </div>
            )}
            
            {/* Badges Overlay */}
            <div className="absolute top-2 left-2 flex flex-col gap-1 items-start z-10">
              {isNoWatermarkReady && (
                <span className="px-1.5 py-0.5 rounded bg-emerald-500/90 backdrop-blur-md text-[9px] font-bold text-white border border-white/10 shadow-sm">
                  No Watermark
                </span>
              )}
              {isUpscaledReady ? (
                <span className="px-1.5 py-0.5 rounded bg-indigo-500/90 backdrop-blur-md text-[9px] font-bold text-white border border-white/10 shadow-sm">
                  Upscaled x2
                </span>
              ) : (
                <span className="px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-md text-[9px] font-bold text-slate-300 border border-white/10 shadow-sm">
                  Not Upscaled
                </span>
              )}
            </div>

            {/* Hover Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-all duration-300">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:opacity-100 group-hover:scale-110">
                <FontAwesomeIcon icon="expand" className="text-xs" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                  {formatDateTime(latestJob.created_at)}
                </p>
              </div>
              <button
                onClick={() => onCopy(displayUrl)}
                className="flex-shrink-0 p-1.5 text-slate-400 hover:text-indigo-500 transition"
                title="Copier le lien"
              >
                <FontAwesomeIcon icon="link" className="text-xs" />
              </button>
            </div>
            
            {bestAvailableJob ? (
              <button
                onClick={() => onDownload(bestAvailableJob)}
                disabled={downloadingId === bestAvailableJob.job_id}
                className={`w-full flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                  downloadingId === bestAvailableJob.job_id
                    ? 'bg-indigo-300 text-white cursor-wait'
                    : 'bg-indigo-600 text-white hover:bg-indigo-500'
                }`}
              >
                <FontAwesomeIcon
                  icon={downloadingId === bestAvailableJob.job_id ? 'spinner' : 'download'}
                  spin={downloadingId === bestAvailableJob.job_id}
                />
                {isUpscaledReady ? 'Télécharger Upscale' : 'Télécharger Sans WM'}
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 py-2 text-xs text-amber-500 font-medium">
                <FontAwesomeIcon icon="spinner" spin />
                Traitement en cours...
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== FULL MODE (Upscale Page) =====
  return (
    <div className={`group relative overflow-hidden rounded-2xl border bg-white dark:bg-slate-900/60 shadow-sm transition-all hover:shadow-md ${
      isHighlighted 
        ? 'border-indigo-500 ring-2 ring-indigo-500/50 animate-pulse' 
        : 'border-slate-200 dark:border-white/10'
    }`}>
      
      {/* ===== MOBILE LAYOUT (< sm) ===== */}
      <div className="flex sm:hidden">
        {/* Thumbnail carré compact */}
        <div
          className="relative flex-shrink-0 cursor-pointer group/preview bg-slate-100 dark:bg-black/20 overflow-hidden w-20 h-auto aspect-square"
          onClick={() => previewUrl && onExpand?.(previewUrl)}
        >
          {previewUrl ? (
            <>
              {!isVideoLoaded && thumbnail && (
                <img src={thumbnail} alt="Thumbnail" className="absolute inset-0 h-full w-full object-cover" />
              )}
              <video
                src={previewUrl}
                className={`h-full w-full object-cover transition-opacity duration-300 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                muted
                loop
                playsInline
                onCanPlay={() => setIsVideoLoaded(true)}
              />
            </>
          ) : thumbnail ? (
            <img src={thumbnail} alt="Preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-400">
              <FontAwesomeIcon icon="video" className="text-lg" />
            </div>
          )}
          
          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm">
              <FontAwesomeIcon icon="expand" className="text-[8px]" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-2 flex flex-col gap-1.5 min-w-0">
          {/* Header row */}
          <div className="flex items-center justify-between gap-1">
            <p className="text-[11px] font-semibold text-slate-900 dark:text-white truncate">
              {formatDateTime(latestJob.created_at)}
            </p>
            <button
              onClick={() => onCopy(displayUrl)}
              className="flex-shrink-0 p-1 text-slate-400 hover:text-indigo-500"
              title="Copier le lien"
            >
              <FontAwesomeIcon icon="link" className="text-[10px]" />
            </button>
          </div>

          {/* Job rows - same info as desktop */}
          <div className="space-y-1">
            {renderMobileJobRow(nwJob, 'Sans Watermark', 'wand-magic-sparkles')}
            {renderMobileJobRow(upJob, 'Upscale ×2', 'infinity')}
          </div>
        </div>
      </div>

      {/* ===== DESKTOP LAYOUT (>= sm) ===== */}
      <div className="hidden sm:flex">
        {/* Preview Section */}
        <div
          className="relative flex-shrink-0 cursor-pointer group/preview bg-slate-100 dark:bg-black/20 overflow-hidden w-36 md:w-40 aspect-video"
          onMouseEnter={() => onHoverPreview?.(latestJob)}
          onClick={() => previewUrl && onExpand?.(previewUrl)}
        >
          {previewUrl ? (
            <>
              {!isVideoLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800 z-20">
                  {thumbnail && (
                    <img 
                      src={thumbnail} 
                      alt="Thumbnail" 
                      className="absolute inset-0 h-full w-full object-cover opacity-50 blur-sm" 
                    />
                  )}
                  <FontAwesomeIcon icon="spinner" spin className="relative z-30 text-indigo-500 text-2xl" />
                </div>
              )}
              <video
                src={previewUrl}
                className={`h-full w-full object-cover transition-opacity duration-500 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                muted
                loop
                playsInline
                onCanPlay={() => setIsVideoLoaded(true)}
                onMouseOver={(e) => isVideoLoaded && e.target.play()}
                onMouseOut={(e) => {
                  e.target.pause();
                  e.target.currentTime = 0;
                }}
              />
            </>
          ) : thumbnail ? (
            <img src={thumbnail} alt="Preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-400">
              <FontAwesomeIcon icon="video" className="text-xl" />
            </div>
          )}
          
          {/* Badges Overlay */}
          <div className="absolute top-2 left-2 flex flex-col gap-1 items-start z-10">
            {isNoWatermarkReady && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-500/90 backdrop-blur-md text-[9px] font-bold text-white border border-white/10 shadow-sm">
                No Watermark
              </span>
            )}
            {isUpscaledReady ? (
              <span className="px-1.5 py-0.5 rounded bg-indigo-500/90 backdrop-blur-md text-[9px] font-bold text-white border border-white/10 shadow-sm">
                Upscaled x2
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-md text-[9px] font-bold text-slate-300 border border-white/10 shadow-sm">
                Not Upscaled
              </span>
            )}
          </div>

          {/* Hover Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/preview:bg-black/30 transition-all duration-300">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur-sm transition-all duration-300 group-hover/preview:opacity-100 group-hover/preview:scale-110">
              <FontAwesomeIcon icon="expand" className="text-xs" />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-3 flex flex-col justify-center gap-2 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm text-slate-900 dark:text-white line-clamp-1">
                {previewData?.title || `Job du ${formatDateTime(latestJob.created_at)}`}
              </h3>
              <a
                href={displayUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-slate-500 hover:text-indigo-500 dark:text-slate-400 truncate block"
              >
                {displayUrl}
              </a>
            </div>
            <button
              onClick={() => onCopy(displayUrl)}
              className="flex-shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-indigo-600 dark:hover:bg-white/10 transition-colors"
              title="Copier le lien source"
            >
              <FontAwesomeIcon icon="link" className="text-xs" />
            </button>
          </div>

          {/* Job Rows */}
          <div className="space-y-1.5">
            {renderJobRow(
              nwJob,
              'Sans Watermark',
              'wand-magic-sparkles',
              <button
                onClick={() => onUpscale?.(displayUrl, 'no_watermark')}
                className="text-xs font-semibold text-slate-400 hover:text-indigo-600 disabled:opacity-50"
                disabled
              >
                Non généré
              </button>
            )}
            {renderJobRow(
              upJob,
              'Upscale ×2 Pro',
              'infinity',
              onUpscale ? (
                <button
                  onClick={() => onUpscale(displayUrl)}
                  className="rounded bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20 transition-colors"
                >
                  <FontAwesomeIcon icon="bolt" className="mr-1" />
                  Lancer · {upscaleCost || 'N/A'} cr
                </button>
              ) : (
                <span className="text-xs font-semibold text-slate-400">Non généré</span>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCard;
